import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageHeader, Empty } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, BookOpen, Trash2, FileText } from "lucide-react";

const STATUS = ["Active", "Draft", "Archived"];
const CATEGORIES = ["Sterility", "Medication", "Equipment", "Documentation", "Patient Care", "General"];
const PILLARS = ["Medication & Sterility", "OT Assistance & Patient Care", "Documentation & Media", "Equipment & Post-Op Management", "Treatment Execution & Results", "Communication & Professional Conduct", "Patient Education & Value Addition"];
const statusColor = (s) => s === "Active" ? "bg-emerald-600" : s === "Draft" ? "bg-[#C9A24B] text-[#0b1120]" : "bg-slate-600";

export default function SOPLibrary() {
  const { user } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState(null);
  const canManage = ["super_admin", "ot_head"].includes(user?.app_role);

  const load = () => { setLoading(true); api.get("/sops").then(({ data }) => setRows(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const filtered = cat === "all" ? rows : rows.filter((r) => r.category === cat);
  const blank = { code: "", title: "", category: "General", pillar: PILLARS[0], content: "", version: "1.0", status: "Active" };
  const openNew = () => { setEdit({ ...blank }); setOpen(true); };
  const openEdit = (r) => { setEdit({ ...r }); setOpen(true); };
  const save = async () => {
    if (!edit.code || !edit.title) { toast.error("Code & title required"); return; }
    try {
      if (edit.id) await api.put(`/sops/${edit.id}`, edit); else await api.post("/sops", edit);
      toast.success("SOP saved"); setOpen(false); load();
    } catch (e) { toast.error("Save failed"); }
  };
  const remove = async (id) => { await api.delete(`/sops/${id}`); toast.success("Deleted"); load(); };
  const inp = "bg-[#0f172a] border border-white/10 rounded-md px-3 py-2 text-sm w-full focus:ring-2 focus:ring-[#C9A24B] focus:outline-none";

  return (
    <div className="animate-fade-up" data-testid="sop-library-page">
      <PageHeader title="OT Training SOP Library" subtitle="Defined Standard Operating Procedures for OT training — fully editable as protocols evolve."
        action={canManage && <button data-testid="add-sop-btn" onClick={openNew} className="flex items-center gap-2 bg-[#C9A24B] text-[#0b1120] font-semibold px-4 py-2 rounded-md hover:brightness-110 transition-all"><Plus size={16} /> New SOP</button>} />

      <div className="flex flex-wrap gap-2 mb-5">
        {["all", ...CATEGORIES].map((c) => (
          <button key={c} data-testid={`sop-cat-${c}`} onClick={() => setCat(c)}
            className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${cat === c ? "bg-[#C9A24B] text-[#0b1120] border-[#C9A24B] font-semibold" : "border-white/10 text-slate-300 hover:bg-white/10"}`}>
            {c === "all" ? "All" : c}
          </button>
        ))}
      </div>

      {loading ? <Empty text="Loading…" /> : filtered.length === 0 ? <Empty text="No SOPs yet." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((r) => (
            <Card key={r.id} className="p-5 hover:-translate-y-1 transition-transform cursor-pointer" data-testid={`sop-card-${r.id}`} onClick={() => setView(r)}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2"><BookOpen size={18} className="text-[#C9A24B]" /><span className="font-mono text-xs text-[#C9A24B]">{r.code}</span></div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full text-white ${statusColor(r.status)}`}>{r.status}</span>
              </div>
              <h4 className="font-medium text-slate-100">{r.title}</h4>
              <p className="text-xs text-slate-400 mt-1">{r.category} · v{r.version}</p>
              <p className="text-xs text-slate-500 mt-2 line-clamp-2">{r.content}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-[10px] text-slate-600">{r.pillar}</span>
                {canManage && <button onClick={(e) => { e.stopPropagation(); openEdit(r); }} className="text-slate-400 hover:text-[#C9A24B]" data-testid={`edit-sop-${r.id}`}><Pencil size={14} /></button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* View SOP */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="text-[#C9A24B]" size={18} /> {view?.code} — {view?.title}</DialogTitle></DialogHeader>
          {view && (
            <div>
              <p className="text-xs text-slate-400 mb-3">{view.category} · {view.pillar} · v{view.version} · Updated by {view.updated_by}</p>
              <pre className="whitespace-pre-wrap text-sm text-slate-200 bg-[#0b1120] rounded-md p-4 border border-white/10 font-sans">{view.content}</pre>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit SOP */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit" : "New"} SOP</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-400">Code</label><input data-testid="sop-code" className={inp} value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.target.value })} placeholder="SOP-150" /></div>
              <div><label className="text-xs text-slate-400">Version</label><input className={inp} value={edit.version} onChange={(e) => setEdit({ ...edit, version: e.target.value })} /></div>
              <div className="col-span-2"><label className="text-xs text-slate-400">Title</label><input data-testid="sop-title" className={inp} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></div>
              <div><label className="text-xs text-slate-400">Category</label><select className={inp} value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
              <div><label className="text-xs text-slate-400">Status</label><select className={inp} value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>{STATUS.map((c) => <option key={c}>{c}</option>)}</select></div>
              <div className="col-span-2"><label className="text-xs text-slate-400">Pillar</label><select className={inp} value={edit.pillar} onChange={(e) => setEdit({ ...edit, pillar: e.target.value })}>{PILLARS.map((p) => <option key={p}>{p}</option>)}</select></div>
              <div className="col-span-2"><label className="text-xs text-slate-400">Procedure / Steps</label><textarea data-testid="sop-content" rows={7} className={inp} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} placeholder="1. Step one…&#10;2. Step two…" /></div>
              <div className="col-span-2 flex gap-2">
                <button data-testid="save-sop-btn" onClick={save} className="flex-1 bg-emerald-600 text-white font-semibold py-2 rounded-md hover:brightness-110 transition-all">Save SOP</button>
                {edit.id && <button onClick={() => { remove(edit.id); setOpen(false); }} className="px-4 bg-[#DC2626] text-white rounded-md"><Trash2 size={16} /></button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
